import React, {useCallback, useEffect, useLayoutEffect} from 'react'
import {Routes} from 'react-router'
import styled from 'styled-components'
import SVGLockSmall from '../assets/icons/lock-small.svg'
import SVGDocs from '../assets/icons/nav/docs.svg'
import {SignInForm} from '../components/auth'
import {Button} from '../components/library/buttons'
import {Icon} from '../components/library/icon/icon'
import {Loader} from '../components/loader'
import {OrganisationAccess} from '../data/workbench-api/generated/types'
import {useAnalytics} from '../lib/analytics/analytics'
import {useAuth} from '../lib/auth'
import {ENVIRONMENT_NAME} from '../lib/env'
import {Route} from '../lib/router'
import {HandleAuthCode} from '../views/auth/code'
import {ConfigurationForm} from './configuration'
import {PluginEnvironmentSwitcher} from './env-switcher'
import {ActivityState, useJourneyBuilderConnection} from './journey-builder'
import {MessagingTest} from './messaging-test'
import {useAllOrganisationAccess} from './org-access'
import {usePostMonger} from './postmonger'

export const App = () => {
  const {auth, authState} = useAuth()
  const analytics = useAnalytics()
  const {organisations: accessibleOrgs} = useAllOrganisationAccess(authState)
  const {connection} = usePostMonger()
  const activityState = useJourneyBuilderConnection(connection)

  const {data, setMetadata} = activityState
  const organisationId = data.metaData.organisationId
  const environmentId = data.metaData.environmentId

  useEffect(() => {
    if (analytics && organisationId) {
      analytics.track({
        event: 'Viewed SFMC Trigger Step in Journey Builder',
        properties: {
          category:
            'external-plugin>salesforce>marketing-cloud-journey-builder',
          org_id: organisationId,
          environment_id: environmentId // optionally present
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analytics, organisationId])

  // default to the only option if there is only one
  useLayoutEffect(() => {
    if (
      accessibleOrgs?.length === 1 &&
      accessibleOrgs[0].environments?.length === 1 &&
      (!organisationId || !environmentId)
    ) {
      setMetadata(c => ({
        ...c,
        organisationId: accessibleOrgs[0].id,
        environmentId: accessibleOrgs[0].environments?.[0]?.id as string
      }))
    }
  }, [accessibleOrgs, organisationId, environmentId, setMetadata])

  const signOut = useCallback(async () => {
    if (organisationId) {
      await analytics.track(
        {
          event: 'Logged out of workbench',
          properties: {
            category: 'general',
            org_id: organisationId
          }
        },
        true
      )
    }
    auth.signOut()
    // hard redirect to purge state
    // setTimeout gives time to send any analytics etc
    // eslint-disable-next-line functional/immutable-data
    setTimeout(() => (window.location.href = '/'), 1000)
  }, [auth, organisationId, analytics])

  if (!authState.loaded) {
    return (
      <>
        <title>SFMC plugin - Atomic Workbench</title>
        <Loader />
      </>
    )
  }

  return (
    <Wrap>
      <title>SFMC plugin - Atomic Workbench</title>
      <Header>
        <img src="/atomic-logo.png" height="20" style={{marginRight: '20px'}} />
        <div className="env-picker">
          {accessibleOrgs ? (
            <PluginEnvironmentSwitcher
              organisations={accessibleOrgs}
              organisationId={organisationId}
              environmentId={environmentId}
              setEnv={(orgId, envId) => {
                setMetadata(c => ({
                  ...c,
                  organisationId: orgId,
                  environmentId: envId
                }))
              }}
            />
          ) : undefined}
        </div>
        <div className="spacer" />
        <div className="actions">
          <InlineWithIcon
            target="_blank"
            href="https://documentation.atomic.io/integration/salesforce-marketing-cloud-journey-builder"
            style={{display: 'flex'}}
          >
            <DropdownItemSmallIcon>
              <SVGDocs />
            </DropdownItemSmallIcon>
            <span>Documentation</span>
          </InlineWithIcon>
        </div>
        <div className="actions">
          {authState.authenticated ? (
            <Button
              onClick={() => {
                signOut()
              }}
              appearance="transparent"
              size="xsmall"
            >
              <DropdownItemSmallIcon>
                <SVGLockSmall />
              </DropdownItemSmallIcon>
              <span style={{fontWeight: 'normal'}}>Log&nbsp;out</span>
            </Button>
          ) : null}
        </div>
      </Header>

      <Body>
        <Routes>
          <Route path="/auth" render={() => <HandleAuthCode />} />
          <Route
            path="/*"
            render={() => (
              <Content {...activityState} accessibleOrgs={accessibleOrgs} />
            )}
          />
        </Routes>

        {ENVIRONMENT_NAME !== 'master' ? (
          <MessagingTest connection={connection} data={activityState.data} />
        ) : null}
      </Body>
    </Wrap>
  )
}

const InlineWithIcon = styled.a`
  color: ${p => p.theme.colors.blue11};
  display: flex;
  align-items: center;
  font-weight: normal;
  font-size: ${p => p.theme.fontSizeXS};
  svg {
    flex: none;
    color: ${p => p.theme.colors.blue11};
  }
`

const DropdownItemSmallIcon = styled.span`
  width: 16px;
  margin-right: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  svg {
    flex: none;
    color: ${p => p.theme.colors.blue11};
  }
`

const Content = ({
  accessibleOrgs,
  ...activityState
}: ActivityState & {accessibleOrgs?: OrganisationAccess[]}) => {
  const {auth, authState} = useAuth()

  if (!authState.authenticated) {
    return (
      <SignInWrap>
        <SignInForm auth={auth} showLogo={false} onComplete={() => undefined} />
      </SignInWrap>
    )
  }

  // Note that this will only show if the state is uninitialised and initActivity hasn't fired.
  // This can happen where the iframe has reloaded e.g. after SSO login
  if (!activityState.isInitialised) {
    return (
      <Warning>
        <div>
          <Icon name="action" />
        </div>
        <WarningDescription>
          <p>Please close and re-open this Atomic activity</p>
        </WarningDescription>
      </Warning>
    )
  }

  const {organisationId, environmentId} = activityState.data.metaData

  if (
    !organisationId ||
    typeof organisationId !== 'string' ||
    !environmentId ||
    typeof environmentId !== 'string'
  ) {
    return <p>Please choose an environment</p>
  }

  // note this needs to come after SignInForm so they can switch logins if necessary
  if (
    accessibleOrgs && // accessible orgs should be undefined until loaded
    missingOrgEnvAccess(organisationId, environmentId, accessibleOrgs)
  ) {
    const access = missingOrgEnvAccess(
      organisationId,
      environmentId,
      accessibleOrgs
    )
    return (
      <Warning>
        <div>
          <Icon name="padlock" />
        </div>
        <WarningDescription>
          <p>
            You don't have access to the atomic
            {access?.org ? '' : ' organization and'} environment configured for
            this activity.
            <br />
            Organization: <code>{organisationId}</code> Environment:{' '}
            <code>{environmentId}</code>
          </p>
        </WarningDescription>
      </Warning>
    )
  }

  return (
    <ConfigurationForm
      organisationId={organisationId}
      environmentId={environmentId}
      {...activityState}
    />
  )
}

const Wrap = styled.div`
  width: 100%;
`
const Header = styled.div`
  border-bottom: 1px solid ${p => p.theme.grey10};
  display: flex;
  padding: 10px 20px;
  align-items: center;

  .spacer {
    flex: 1;
  }
  .actions,
  .env-switcher {
    margin-left: 20px;
  }
`
const Body = styled.div`
  padding: 20px;
`

const SignInWrap = styled.div`
  max-width: 500px;
  padding: 20px;
  margin: 0 auto;
`
const missingOrgEnvAccess = (
  orgId: string,
  envId: string,
  organisationAccess: OrganisationAccess[]
): {org: boolean; env: false} | null => {
  const orgAccess = organisationAccess.find(org => org.id === orgId)
  if (!orgAccess) return {org: false, env: false}
  const envAccess = orgAccess.environments?.find(env => env?.id === envId)
  if (!envAccess) return {org: true, env: false}
  return null // has access
}

const Warning = styled.div`
  border: 1px solid ${p => p.theme.grey10};
  border-radius: 4px;
  display: flex;
  align-items: center;
  padding: 20px;
  gap: 20px;
`

const WarningDescription = styled.div`
  display: flex;
  justify-content: center;
`
